import { dureeEnMots } from '@nextilt/commun';
import type { TempsDeJAi, Vivier } from '../partage/api/types';
import { REGLAGES_PAR_DEFAUT, type ReglagesDeMaintenant } from './reglagesDeMaintenant';
import {
  auMilieu,
  debloquantTropLong,
  majuscule,
  MOTS_COURAGE,
  MOTS_DU_TEMPS,
  MOTS_LIEU,
  PLUS_LONG_QUE,
  TOUS_LES_PROJETS,
  TOUT_LE_TEMPS,
} from '../partage/vocabulaire';

/**
 * Le panneau Réglages de « Maintenant » au téléphone, sans rendu (chantier `telephone-cadre`,
 * ticket 03, `design/telephone.html`, piste « Maintenant », décision d'Igor du 2026-10-05,
 * revue 5) : ce que dit le bouton Réglages, combien de tâches répondent aux réglages du panneau
 * (le mot du bouton : `voirLesPossibles`), et la ligne du débloquant qui ne tient pas dans le
 * temps choisi.
 */

type UnDebloquant = Vivier['debloquants'][number];

/** Un réglage s'écarte des défauts, ou un projet est choisi : le bouton dit le filtre, et porte sa croix. */
export function estFiltre(reglages: ReglagesDeMaintenant, projet: string | null): boolean {
  return (
    projet !== null ||
    reglages.temps !== REGLAGES_PAR_DEFAUT.temps ||
    reglages.lieu !== REGLAGES_PAR_DEFAUT.lieu ||
    reglages.courage !== REGLAGES_PAR_DEFAUT.courage
  );
}

/**
 * Ce que dit le bouton Réglages. Sans filtre : « Tous les projets · tout le temps ». Filtré : ce
 * qui est choisi, en clair, dans l'ordre du panneau (« Déménager · 10 min · chez moi »). Le titre
 * du projet s'écrit tel que tu l'as tapé.
 */
export function resumeDesReglages(reglages: ReglagesDeMaintenant, projetTitre: string | null): string {
  if (!estFiltre(reglages, projetTitre)) return `${TOUS_LES_PROJETS} · ${auMilieu(TOUT_LE_TEMPS)}`;
  const mots = [
    reglages.temps ? MOTS_DU_TEMPS[reglages.temps] : null,
    reglages.lieu ? MOTS_LIEU[reglages.lieu] : null,
    reglages.courage !== REGLAGES_PAR_DEFAUT.courage ? MOTS_COURAGE[reglages.courage] : null,
  ]
    .filter((m): m is string => Boolean(m))
    .map(auMilieu);
  if (projetTitre !== null) return [projetTitre, ...mots].join(' · ');
  return mots.map((m, i) => (i === 0 ? majuscule(m) : m)).join(' · ');
}

/**
 * Combien de tâches répondent : ce que le serveur compte, par projet, dans ses pastilles (tout ce
 * qui passe les réglages, sous l'eau et pliées comprises, Prioritaire aussi). Les pastilles se
 * comptent toujours sur tous les projets ; un projet absent n'a rien qui passe.
 */
export function possibles(pastilles: Vivier['pastilles'], projet: string | null): number {
  if (projet !== null) return pastilles.find((p) => p.id === projet)?.combien ?? 0;
  return pastilles.reduce((somme, p) => somme + p.combien, 0);
}

/**
 * La ligne du débloquant qui ne tient pas dans le temps choisi, à la place de sa carte (maquette :
 * « Le débloquant de ce projet prend 1 h : « M'inscrire à France Travail ». »). Le serveur dit
 * l'écart (`ecarts`) ; ici, on le met en mots. Rien quand il tient dans le temps : un écart de
 * lieu ou de courage garde la carte et sa ligne d'écarts.
 */
export function debloquantHorsDuTemps(
  debloquant: UnDebloquant | null,
  temps: TempsDeJAi | null,
  deCeProjet: boolean
): string | null {
  if (!debloquant || !temps || !debloquant.ecarts.includes('temps')) return null;
  const { effortMin, titre } = debloquant.bulle;
  const duree = effortMin != null ? `prend ${dureeEnMots(effortMin)}` : `est ${auMilieu(PLUS_LONG_QUE[temps])}`;
  return debloquantTropLong(titre, duree, deCeProjet);
}
